import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { theme } from 'antd';
import { useAuthStore } from '../stores/authStore';

export type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  mode: ThemeMode;
  isDark: boolean;
  setTheme: (mode: ThemeMode) => void;
  antdTheme: any;
  monacoTheme: string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light');
  const [isDark, setIsDark] = useState(false);
  const { user } = useAuthStore();

  // Функція для визначення системної теми
  const getSystemTheme = (): boolean => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  // Функція для обчислення isDark на основі mode
  const calculateIsDark = (currentMode: ThemeMode): boolean => {
    if (currentMode === 'auto') {
      return getSystemTheme();
    }
    return currentMode === 'dark';
  };

  // Ініціалізація теми
  useEffect(() => {
    // Спочатку беремо з localStorage
    const savedTheme = localStorage.getItem('theme') as ThemeMode;
    
    // Якщо є збережена тема, використовуємо її
    if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
      setMode(savedTheme);
      const newIsDark = calculateIsDark(savedTheme);
      setIsDark(newIsDark);
      document.body.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
    } else {
      // Інакше використовуємо світлу тему за замовчуванням
      const defaultTheme: ThemeMode = 'light';
      setMode(defaultTheme);
      const newIsDark = calculateIsDark(defaultTheme);
      setIsDark(newIsDark);
      document.body.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
    }
  }, [user]);

  // Слухаємо зміни системної теми
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      if (mode === 'auto') {
        const newIsDark = getSystemTheme();
        setIsDark(newIsDark);
        document.body.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [mode]);

  // Функція для зміни теми
  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
    const newIsDark = calculateIsDark(newMode);
    setIsDark(newIsDark);
    
    // Встановлюємо data-theme атрибут на body
    document.body.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
    
    // Зберігаємо в localStorage
    localStorage.setItem('theme', newMode);
  };

  // Налаштування Ant Design теми
  const antdTheme = {
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: '#1890ff',
      borderRadius: 6,
      wireframe: false,
      // Додаткові налаштування для темної теми
      ...(isDark && {
        colorBgContainer: '#141414',
        colorBgElevated: '#1f1f1f',
        colorBgLayout: '#000000',
        colorBorder: '#424242',
        colorBorderSecondary: '#303030',
        colorText: '#ffffff',
        colorTextSecondary: '#a6a6a6',
        colorTextTertiary: '#737373',
        colorTextQuaternary: '#595959',
      }),
    },
    components: {
      Layout: {
        headerBg: isDark ? '#1f1f1f' : '#ffffff',
        siderBg: isDark ? '#1f1f1f' : '#ffffff',
        bodyBg: isDark ? '#000000' : '#f5f5f5',
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: isDark ? '#1890ff' : '#e6f7ff',
        itemActiveBg: isDark ? '#262626' : '#f5f5f5',
        itemColor: isDark ? '#ffffff' : '#000000',
        itemSelectedColor: isDark ? '#ffffff' : '#1890ff',
      },
      Card: {
        colorBgContainer: isDark ? '#1f1f1f' : '#ffffff',
      },
      Table: {
        colorBgContainer: isDark ? '#1f1f1f' : '#ffffff',
        headerBg: isDark ? '#262626' : '#fafafa',
      },
      Modal: {
        contentBg: isDark ? '#1f1f1f' : '#ffffff',
        headerBg: isDark ? '#1f1f1f' : '#ffffff',
      },
      Form: {
        itemMarginBottom: 16,
      },
    },
  };

  // Monaco Editor тема
  const monacoTheme = isDark ? 'vs-dark' : 'vs-light';

  const value: ThemeContextType = {
    mode,
    isDark,
    setTheme,
    antdTheme,
    monacoTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 